/**
 * Single source of truth for the Admin nav tabs.
 *
 * EVERY new admin tab MUST be added here — both the sidebar (AdminSidebar)
 * and any future flat-tab-nav read this config so we never end up with a
 * tab visible in one nav but missing from the other.
 *
 * Structure:
 *   - groups: ordered list of group objects, each with `label`, `icon`, `color`
 *     and an `items` array of `{ id, label, icon }` tab descriptors.
 *   - permissions: behaviour map for canSeeTab() helper. Keys are tab ids;
 *     values are one of:
 *       "always"   → visible to everyone authenticated
 *       "admin"    → only admin / super_admin / hr_head (or if explicitly in allowedTabs)
 *       "default"  → only when allowedTabs includes the tab id
 *
 * The standalone DoozyBot button (rendered above the groups) is treated as
 * its own ungrouped tab below.
 */
import {
  Calendar, DollarSign, Users, UserCheck, Star, Shield,
  Package, Target, TrendingUp, Tag, Gift, Home, MapPin,
  Navigation, Bot, BarChart3, Settings, Megaphone, Sparkles,
  Image, GraduationCap, Activity, Zap, Brain, Trash2, ClipboardList,
} from "lucide-react";

export const ADMIN_TAB_GROUPS = [
  {
    label: "Core",
    icon: BarChart3,
    color: "text-[#DC283C]",
    items: [
      { id: "smart_analytics", label: "Analytics", icon: TrendingUp },
      { id: "calendar", label: "Bookings", icon: Calendar },
      { id: "crm", label: "CRM & WhatsApp", icon: Users },
      { id: "tracker", label: "Field Tracker", icon: Navigation },
    ],
  },
  {
    label: "Sales & Marketing",
    icon: TrendingUp,
    color: "text-[#FF9F1C]",
    items: [
      { id: "sales_manager", label: "Sales", icon: TrendingUp },
      { id: "sales_dashboard", label: "My Leads", icon: Target },
      { id: "doozyad", label: "DoozyAd", icon: Megaphone },
      { id: "offers", label: "Offers", icon: Tag },
      { id: "referrals", label: "Referrals", icon: Gift },
      { id: "social", label: "Social", icon: Star },
    ],
  },
  {
    label: "Operations",
    icon: Home,
    color: "text-indigo-500",
    items: [
      { id: "ops", label: "Ops Team", icon: Home },
      { id: "smart_scheduler", label: "Scheduler", icon: Calendar },
      { id: "vendors", label: "Vendors", icon: Package },
      { id: "materials", label: "Material Rates", icon: ClipboardList },
      { id: "decorator_registrations", label: "Decorator Registrations", icon: ClipboardList },
      { id: "pincodes", label: "Pincodes", icon: MapPin },
    ],
  },
  {
    label: "Admin & Settings",
    icon: Settings,
    color: "text-[#475569]",
    items: [
      { id: "team", label: "Team & Roles", icon: UserCheck },
      { id: "doozyhr", label: "DoozyHR", icon: Brain },
      { id: "accounts", label: "Accounts", icon: DollarSign },
      { id: "doozyguard", label: "DoozyGuard", icon: Activity },
      { id: "doozyboss", label: "DoozyBoss", icon: Zap },
      { id: "training", label: "Training", icon: GraduationCap },
      { id: "academy", label: "🎓 Academy", icon: GraduationCap },
      { id: "finance", label: "Finance", icon: DollarSign },
      { id: "security", label: "Security", icon: Shield },
      { id: "rbac", label: "Access Control", icon: Shield },
      { id: "bot_training", label: "Bot Training", icon: Megaphone },
      { id: "creators_analytics", label: "Creator Analytics", icon: Sparkles },
      { id: "ai_creations", label: "AI Creations", icon: Image },
      { id: "ai_gallery", label: "Reference Gallery", icon: Image },
      { id: "magic_scale", label: "Magic Scale", icon: Activity },
      { id: "data_clean", label: "Data Clean", icon: Trash2 },
    ],
  },
];

/** Standalone hero tab rendered above the groups (DoozyBot button). */
export const ADMIN_STANDALONE_TAB = {
  id: "doozybot",
  label: "DoozyBot",
  icon: Bot,
};

/**
 * Tabs that require admin-style elevated access. If a tab is in this set,
 * it's visible if the user is admin/super_admin/hr_head OR if it's been
 * explicitly granted via `allowedTabs`. Tabs NOT in this set use the
 * default `allowedTabs.includes(id)` rule.
 */
export const ADMIN_ELEVATED_TABS = new Set([
  "vendors", "pincodes", "bot_training", "doozybot", "rbac",
  "creators_analytics", "ai_gallery", "reference_gallery",
  "magic_scale", "decorator_registrations",
  "smart_analytics", "smart_scheduler",
]);

/** Tabs that anyone authenticated can see, regardless of allowedTabs. */
export const ADMIN_ALWAYS_VISIBLE_TABS = new Set(["training", "academy"]);

/** Tabs restricted to Super Admin (role==='admin') ONLY — never visible to
 *  CFO / Ops Head / HR Head / Sales / Security etc. even if listed in their
 *  allowedTabs. Must mirror the backend `verify_super_admin_header` guard. */
export const ADMIN_SUPER_ONLY_TABS = new Set(["data_clean"]);

/**
 * Decide whether a given tab should be visible to the current user.
 * Single helper used by every nav surface so visibility never drifts.
 */
export const canSeeAdminTab = (tabId, { allowedTabs = [], auth = {} } = {}) => {
  if (ADMIN_SUPER_ONLY_TABS.has(tabId)) {
    return auth.role === "admin";
  }
  if (ADMIN_ALWAYS_VISIBLE_TABS.has(tabId)) return true;
  if (ADMIN_ELEVATED_TABS.has(tabId)) {
    return allowedTabs.includes(tabId)
      || auth.role === "admin"
      || auth.role === "super_admin"
      || auth.role === "hr_head";
  }
  return allowedTabs.includes(tabId);
};

/** Flat list of every tab id known to the admin shell — handy for sanity checks. */
export const ALL_ADMIN_TAB_IDS = [
  ADMIN_STANDALONE_TAB.id,
  ...ADMIN_TAB_GROUPS.flatMap(g => g.items.map(i => i.id)),
];
